/**
 * The words sent to somebody's own agent when a screen asks it how much of a project is done.
 *
 * Kept here, and shown in full before it is sent: a command written by a screen and delivered to
 * somebody's agent should never be a surprise.
 */

import type { SpecSummary } from './types'

/**
 * What the agent is asked, verbatim — shown before it is sent, because a command written by a
 * screen and delivered to somebody's agent should never be a surprise.
 */
export function estimatePrompt(project: { key: string; name: string }, specs: SpecSummary[]): string {
  const list = specs.length
    ? specs.map((spec) => `- ${spec.kind === 'Prd' ? 'PRD' : 'SDD'} ${spec.key}: ${spec.title}${spec.documentPath ? ` (${spec.documentPath})` : ''}`).join('\n')
    : '- (Vephus has no SDD or PRD for this project yet: read the specification documents in the repository.)'

  return [
    `Estimate how much of the project ${project.key} (${project.name}) is delivered.`,
    '',
    'Read, in this order:',
    list,
    '- the open bugs for the project (devpulse_bugs_list), which are work still to do;',
    '- the code in the repository you have open, to see what each document describes is actually built.',
    '',
    'Then call devpulse_report_estimate with projectKey, percent, a one-line rationale, and a figure per SDD.',
    'Estimate from what you looked at. If a document is out of date or you cannot see the repository, say so',
    'in the rationale instead of guessing, and give no percent.',
  ].join('\n')
}
